/**
 * Staff bypass for the maintenance screen (web only). Opening any page with `?maintenance-bypass=<token>`
 * stores the token in this browser; the published status document carries only the token's SHA-256,
 * and a browser whose token matches sees the platform — with a banner — while customers see the
 * maintenance screen. It lifts the screen, nothing more: it grants no access to anything.
 */

export const BYPASS_PARAM = "maintenance-bypass";
const BYPASS_OFF = "off";

/**
 * Takes the bypass parameter out of a query string. `token` is undefined when the parameter is absent
 * and null when it asks for the bypass to be removed ("off", or empty); `search` is the rest.
 */
export function takeBypassParam(search: string): { token: string | null | undefined; search: string } {
  const params = new URLSearchParams(search);
  if (!params.has(BYPASS_PARAM)) return { token: undefined, search };
  const value = params.get(BYPASS_PARAM) ?? "";
  params.delete(BYPASS_PARAM);
  const rest = params.toString();
  return { token: value && value !== BYPASS_OFF ? value : null, search: rest ? `?${rest}` : "" };
}

/** Rejects where `crypto.subtle` is unavailable (plain-http origins), which simply means no bypass. */
export async function sha256Hex(text: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(text));
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
}
